import { useEffect, useState } from "react";
import API_BASE_URL from "../config";
import { useAuth } from "../context/AuthContext";

/* 🔔 Toast */
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* ⚠️ Sweet Alert */
import Swal from "sweetalert2";

/* 🎯 Icons */
import {
    FaCalendarAlt,
    FaSearch,
    FaPlus,
    FaEdit,
    FaTrash,
    FaTimes,
    FaCheckCircle,
    FaClock,
    FaUser,
    FaMapMarkerAlt,
    FaUserTie,
    FaComments,
    FaGavel,
    FaExclamationTriangle,
    FaCheck,
    FaCalendarCheck
} from "react-icons/fa";
import {
    MdOutlineEventAvailable,
    MdOutlinePendingActions,
    MdOutlineCheckCircleOutline,
    MdOutlineCancel
} from "react-icons/md";
import {
    FiRefreshCw,
    FiDownload
} from "react-icons/fi";
import "../styles/SolicitorPlanner.css";
import "../styles/SolicitorCases.css";

export default function SolicitorPlanner() {
    const { token } = useAuth();

    const [hearings, setHearings] = useState([]);
    const [filteredHearings, setFilteredHearings] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);

    // 🔥 TABS STATE
    const [activeTab, setActiveTab] = useState("incomplete");

    const [editing, setEditing] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);

    const [cases, setCases] = useState([]);
    const [selectedCaseId, setSelectedCaseId] = useState("");
    const [exportLoading, setExportLoading] = useState(false);
    const [planName, setPlanName] = useState(null);

    const [form, setForm] = useState({
        hearingDateTime: "",
        courtName: "",
        courtLocation: "",
        judgeName: "",
        remarks: "",
        statusAtHearing: "INCOMPLETE"
    });

    /* ======================
       LOAD HEARINGS & CASES
    ====================== */
    useEffect(() => {
        fetchHearings();
        fetchCases();
        fetchUserPlan();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchHearings = async () => {
        try {
            setLoading(true);
            const res = await fetch(
                `${API_BASE_URL}/api/cases/hearings/calendar`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = await res.json();
            setHearings(data.hearings || []);
        } catch (error) {
            console.error("Fetch hearings error:", error);
            toast.error("Failed to load hearings");
        } finally {
            setLoading(false);
        }
    };

    const fetchUserPlan = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const data = await res.json();

            setPlanName(data?.user?.subscription?.planName || "FREE");

        } catch (err) {
            console.error("Plan fetch error:", err);
        }
    };

    const fetchCases = async () => {
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/cases/solicitor`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = await res.json();
            setCases(data.cases || []);
        } catch (error) {
            console.error("Fetch cases error:", error);
            toast.error("Failed to load cases");
        }
    };

    /* ======================
   EXPORT HEARINGS
====================== */
    const handleExport = async () => {
        if (planName === "FREE") {
            toast.error("Upgrade plan to export Excel");
            return;
        }
        if (filteredHearings.length === 0) {
            toast.warning("No hearings to export");
            return;
        }

        try {
            setExportLoading(true);

            const res = await fetch(
                `${API_BASE_URL}/api/cases/hearings/export/excel?type=${activeTab}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (!res.ok) throw new Error("Export failed");

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download =
                activeTab === "incomplete"
                    ? "Incomplete_Hearings.xlsx"
                    : "Completed_Hearings.xlsx";

            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success("Hearings exported successfully");

        } catch (error) {
            console.error("Export error:", error);
            toast.error("Failed to export Excel");
        } finally {
            setExportLoading(false);
        }
    };


    /* ======================
       FILTER & SEARCH LOGIC
    ====================== */
    useEffect(() => {
        let result = hearings;

        // Filter by tab
        if (activeTab === "incomplete") {
            result = result.filter(h => h.statusAtHearing === "INCOMPLETE");
        } else if (activeTab === "completed") {
            result = result.filter(h => h.statusAtHearing === "COMPLETED");
        }

        // Filter by search
        if (search.trim()) {
            const searchTerm = search.toLowerCase();
            result = result.filter(
                h =>
                    h.userDetails?.name?.toLowerCase().includes(searchTerm) ||
                    h.userDetails?.email?.toLowerCase().includes(searchTerm) ||
                    h.courtDetails?.courtName?.toLowerCase().includes(searchTerm) ||
                    h.courtDetails?.judgeName?.toLowerCase().includes(searchTerm)
            );
        }

        setFilteredHearings(result);
    }, [search, hearings, activeTab]);

    /* ======================
       DATE UTILS
    ====================== */
    const toLocalDateTimeInput = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const offset = date.getTimezoneOffset();
        const localDate = new Date(date.getTime() - offset * 60000);
        return localDate.toISOString().slice(0, 16);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        return new Date(dateString).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    /* ======================
       ADD HEARING
    ====================== */
    const addHearing = async () => {
        if (!selectedCaseId) {
            toast.error("Please select a case");
            return;
        }

        if (!form.hearingDateTime || !form.courtName) {
            toast.error("Hearing date and court name are required");
            return;
        }

        try {
            await fetch(
                `${API_BASE_URL}/api/cases/${selectedCaseId}/hearings`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(form)
                }
            );

            setShowAddModal(false);
            resetForm();
            toast.success("Hearing added successfully");
            fetchHearings();
        } catch (error) {
            console.error("Add hearing error:", error);
            toast.error("Failed to add hearing");
        }
    };

    /* ======================
       EDIT HEARING
    ====================== */
    const startEdit = (h) => {
        setEditing(h);
        setForm({
            hearingDateTime: toLocalDateTimeInput(h.hearingDateTime),
            courtName: h.courtDetails?.courtName || "",
            courtLocation: h.courtDetails?.courtLocation || "",
            judgeName: h.courtDetails?.judgeName || "",
            remarks: h.courtDetails?.remarks || "",
            statusAtHearing: h.statusAtHearing || "INCOMPLETE"
        });
    };

    const saveEdit = async () => {
        if (!form.hearingDateTime || !form.courtName) {
            toast.error("Hearing date and court name are required");
            return;
        }

        try {
            await fetch(
                `${API_BASE_URL}/api/cases/${editing.caseId}/hearings/${editing._id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        hearingDateTime: form.hearingDateTime,
                        statusAtHearing: form.statusAtHearing,
                        courtDetails: {
                            courtName: form.courtName,
                            courtLocation: form.courtLocation,
                            judgeName: form.judgeName,
                            remarks: form.remarks
                        }
                    })
                }
            );

            setEditing(null);
            toast.success("Hearing updated successfully");
            fetchHearings();
        } catch (error) {
            console.error("Update hearing error:", error);
            toast.error("Failed to update hearing");
        }
    };

    /* ======================
       ACTIONS WITH CONFIRMATION
    ====================== */
    const cancelHearing = async (h) => {
        const confirm = await Swal.fire({
            title: "Cancel Hearing?",
            text: "This will mark the hearing as cancelled",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Cancel",
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#6b7280"
        });

        if (!confirm.isConfirmed) return;

        try {
            await fetch(
                `${API_BASE_URL}/api/cases/${h.caseId}/hearings/${h._id}/cancel`,
                { method: "PUT", headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Hearing cancelled successfully");
            fetchHearings();
        } catch (error) {
            toast.error("Failed to cancel hearing");
        }
    };

    const deleteHearing = async (h) => {
        const confirm = await Swal.fire({
            title: "Delete Hearing?",
            text: "This action cannot be undone",
            icon: "error",
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#6b7280"
        });

        if (!confirm.isConfirmed) return;

        try {
            await fetch(
                `${API_BASE_URL}/api/cases/${h.caseId}/hearings/${h._id}`,
                { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Hearing deleted successfully");
            fetchHearings();
        } catch (error) {
            toast.error("Failed to delete hearing");
        }
    };

    const updateStatusQuick = async (h, status) => {
        try {
            await fetch(
                `${API_BASE_URL}/api/cases/${h.caseId}/hearings/${h._id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ statusAtHearing: status })
                }
            );
            toast.success(`Hearing marked as ${status.toLowerCase()}`);
            fetchHearings();
        } catch (error) {
            toast.error("Failed to update status");
        }
    };

    const resetForm = () => {
        setForm({
            hearingDateTime: "",
            courtName: "",
            courtLocation: "",
            judgeName: "",
            remarks: "",
            statusAtHearing: "INCOMPLETE"
        });
        setSelectedCaseId("");
    };

    /* ======================
       FILTER CASES FOR ADD HEARING
    ====================== */
    const selectableCases = cases.filter(
        c => !["DISMISSED", "SETTLED"].includes(c.caseStatus)
    );

    /* ======================
       RENDER STATUS BADGE
    ====================== */
    const renderStatusBadge = (status) => {
        const config = {
            INCOMPLETE: {
                icon: <MdOutlinePendingActions size={12} />,
                label: "Incomplete",
                color: "#f59e0b",
                bgColor: "#fef3c7"
            },
            COMPLETED: {
                icon: <FaCheckCircle size={12} />,
                label: "Completed",
                color: "#10b981",
                bgColor: "#d1fae5"
            },
            CANCELLED: {
                icon: <MdOutlineCancel size={12} />,
                label: "Cancelled",
                color: "#dc2626",
                bgColor: "#fee2e2"
            }
        }[status] || {
            icon: <FaExclamationTriangle size={12} />,
            label: "Unknown",
            color: "#6b7280",
            bgColor: "#f3f4f6"
        };

        return (
            <span
                className="planner-status-badge"
                style={{
                    backgroundColor: config.bgColor,
                    color: config.color
                }}
            >
                {config.icon}
                <span>{config.label}</span>
            </span>
        );
    };

    return (
        <div className="solicitor-planner-wrapper">
            {/* 🔔 Toast Container */}
            <ToastContainer position="top-right" autoClose={2500} />

            {/* 🔹 HEADER */}
            <div className="planner-header">
                <div className="planner-header-left">
                    <h1 className="planner-page-title">Hearing Planner</h1>
                    <p className="planner-page-subtitle">
                        Manage and schedule court hearings efficiently
                    </p>
                </div>
                <div className="planner-header-right">
                    <button
                        className="planner-refresh-btn"
                        onClick={fetchHearings}
                        disabled={loading}
                    >
                        <FiRefreshCw size={16} />
                        <span>{loading ? "Refreshing..." : "Refresh"}</span>
                    </button>
                    <button
                        className="case-export-btn"
                        onClick={handleExport}
                        disabled={exportLoading || filteredHearings.length === 0 || planName === "FREE"}
                        title={filteredHearings.length === 0 ? "No hearings to export" : planName === "FREE" ? "Upgrade plan to export Excel" : "Export to Excel"}

                    >
                        {exportLoading ? (
                            <>
                                <div className="case-export-btn-spinner"></div>
                                <span>Exporting...</span>
                            </>
                        ) : (
                            <>
                                <FiDownload size={18} />
                                <span style={{ color: "white" }}>
                                    Export ({filteredHearings.length})
                                </span>
                            </>
                        )}
                    </button>
                    <button
                        className="planner-add-btn"
                        onClick={() => setShowAddModal(true)}
                    >
                        <FaPlus size={16} />
                        <span>Add Hearing</span>
                    </button>
                </div>
            </div>

            {/* 🔥 STATS CARDS */}
            <div className="planner-stats-grid">
                <div className="planner-stat-card planner-stat-incomplete">
                    <div className="planner-stat-icon">
                        <MdOutlinePendingActions size={24} />
                    </div>
                    <div className="planner-stat-content">
                        <h3 className="planner-stat-value">
                            {hearings.filter(h => h.statusAtHearing === "INCOMPLETE").length}
                        </h3>
                        <p className="planner-stat-label">Incomplete</p>
                        <span className="planner-stat-subtext">Pending Hearings</span>
                    </div>
                </div>

                <div className="planner-stat-card planner-stat-completed">
                    <div className="planner-stat-icon">
                        <MdOutlineCheckCircleOutline size={24} />
                    </div>
                    <div className="planner-stat-content">
                        <h3 className="planner-stat-value">
                            {hearings.filter(h => h.statusAtHearing === "COMPLETED").length}
                        </h3>
                        <p className="planner-stat-label">Completed</p>
                        <span className="planner-stat-subtext">Finished Hearings</span>
                    </div>
                </div>

                <div className="planner-stat-card planner-stat-cancelled">
                    <div className="planner-stat-icon">
                        <MdOutlineCancel size={24} />
                    </div>
                    <div className="planner-stat-content">
                        <h3 className="planner-stat-value">
                            {hearings.filter(h => h.statusAtHearing === "CANCELLED").length}
                        </h3>
                        <p className="planner-stat-label">Cancelled</p>
                        <span className="planner-stat-subtext">Cancelled Hearings</span>
                    </div>
                </div>

                <div className="planner-stat-card planner-stat-total">
                    <div className="planner-stat-icon">
                        <FaCalendarAlt size={24} />
                    </div>
                    <div className="planner-stat-content">
                        <h3 className="planner-stat-value">{hearings.length}</h3>
                        <p className="planner-stat-label">Total</p>
                        <span className="planner-stat-subtext">All Hearings</span>
                    </div>
                </div>
            </div>

            {/* 🔥 MAIN CONTENT */}
            <div className="planner-content">
                {/* TABS & SEARCH */}
                <div className="planner-controls">
                    <div className="planner-tabs">
                        <button
                            className={`planner-tab-btn ${activeTab === "incomplete" ? "planner-tab-active" : ""}`}
                            onClick={() => setActiveTab("incomplete")}
                        >
                            <MdOutlinePendingActions size={16} />
                            <span>Incomplete ({hearings.filter(h => h.statusAtHearing === "INCOMPLETE").length})</span>
                        </button>
                        <button
                            className={`planner-tab-btn ${activeTab === "completed" ? "planner-tab-active" : ""}`}
                            onClick={() => setActiveTab("completed")}
                        >
                            <MdOutlineCheckCircleOutline size={16} />
                            <span>Completed ({hearings.filter(h => h.statusAtHearing === "COMPLETED").length})</span>
                        </button>
                    </div>

                    <div className="planner-search-container">
                        <FaSearch size={16} className="planner-search-icon" />
                        <input
                            type="text"
                            className="planner-search-input"
                            placeholder="Search by client, court, or judge..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {/* 🔥 TABLE */}
                <div className="planner-table-container">
                    {loading ? (
                        <div className="planner-loading-state">
                            <div className="planner-loading-spinner"></div>
                            <p>Loading hearings...</p>
                        </div>
                    ) : filteredHearings.length === 0 ? (
                        <div className="planner-empty-state">
                            <MdOutlineEventAvailable size={48} />
                            <p>No {activeTab === "incomplete" ? "incomplete" : "completed"} hearings found</p>
                            <small>Try changing your search or tab selection</small>
                        </div>
                    ) : (
                        <div className="planner-table-wrapper">
                            <table className="planner-table">
                                <thead>
                                    <tr>
                                        <th>Client</th>
                                        <th>Date & Time</th>
                                        <th>Court Details</th>
                                        <th>Remarks</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredHearings.map(h => (
                                        <tr key={h._id}>
                                            <td>
                                                <div className="planner-client-info">
                                                    <div className="planner-client-avatar">
                                                        {h.userId?.userProfile?.photo ? (
                                                            <img
                                                                src={h.userId.userProfile.photo}
                                                                alt={h.userDetails?.name}
                                                                className="planner-avatar-img"
                                                            />
                                                        ) : (
                                                            <div className="planner-avatar-fallback">
                                                                {h.userDetails?.name?.charAt(0)?.toUpperCase()}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="planner-client-details">
                                                        <div className="planner-client-name">
                                                            {h.userDetails?.name || "Unknown Client"}
                                                        </div>
                                                        <div className="planner-client-email">
                                                            {h.userDetails?.email || "No email"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="planner-datetime">
                                                    <div className="planner-date">
                                                        <FaCalendarAlt size={12} />
                                                        <span>{formatDate(h.hearingDateTime)}</span>
                                                    </div>
                                                    <div className="planner-time">
                                                        <FaClock size={12} />
                                                        <span>{formatTime(h.hearingDateTime)}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="planner-court-details">
                                                    <div className="planner-court-name">
                                                        <FaGavel size={12} />
                                                        <span>{h.courtDetails?.courtName || "-"}</span>
                                                    </div>
                                                    {h.courtDetails?.courtLocation && (
                                                        <div className="planner-court-location">
                                                            <FaMapMarkerAlt size={12} />
                                                            <span>{h.courtDetails.courtLocation}</span>
                                                        </div>
                                                    )}
                                                    {h.courtDetails?.judgeName && (
                                                        <div className="planner-judge">
                                                            <FaUserTie size={12} />
                                                            <span>{h.courtDetails.judgeName}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="planner-remarks">
                                                    <FaComments size={12} />
                                                    <span>{h.courtDetails?.remarks || "No remarks"}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="planner-status-cell">
                                                    {renderStatusBadge(h.statusAtHearing)}
                                                    <select
                                                        className="planner-status-select"
                                                        value={h.statusAtHearing || "INCOMPLETE"}
                                                        onChange={(e) => updateStatusQuick(h, e.target.value)}
                                                    >
                                                        <option value="INCOMPLETE">Incomplete</option>
                                                        <option value="COMPLETED">Completed</option>
                                                        <option value="CANCELLED">Cancelled</option>
                                                    </select>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="planner-actions">
                                                    <button
                                                        className="planner-action-btn planner-edit-btn"
                                                        onClick={() => startEdit(h)}
                                                        title="Edit Hearing"
                                                    >
                                                        <FaEdit size={14} />
                                                    </button>
                                                    {h.statusAtHearing !== "COMPLETED" && (
                                                        <button
                                                            className="planner-action-btn planner-cancel-btn"
                                                            onClick={() => cancelHearing(h)}
                                                            title="Cancel Hearing"
                                                        >
                                                            <FaTimes size={14} />
                                                        </button>
                                                    )}
                                                    <button
                                                        className="planner-action-btn planner-delete-btn"
                                                        onClick={() => deleteHearing(h)}
                                                        title="Delete Hearing"
                                                    >
                                                        <FaTrash size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* 🔥 ADD/EDIT MODAL */}
            {(showAddModal || editing) && (
                <div className="planner-modal-overlay">
                    <div className="planner-modal">
                        <div className="planner-modal-header">
                            <h3>
                                <FaCalendarAlt size={20} />
                                <span>{editing ? "Edit Hearing" : "Schedule New Hearing"}</span>
                            </h3>
                            <button
                                className="planner-modal-close"
                                onClick={() => {
                                    setShowAddModal(false);
                                    setEditing(null);
                                    resetForm();
                                }}
                            >
                                <FaTimes size={20} />
                            </button>
                        </div>

                        <div className="planner-modal-body">
                            {!editing && (
                                <div className="planner-form-group">
                                    <label>
                                        <FaUser size={14} />
                                        <span>Select Case *</span>
                                    </label>
                                    <select
                                        className="planner-form-select"
                                        value={selectedCaseId}
                                        onChange={e => setSelectedCaseId(e.target.value)}
                                    >
                                        <option value="">Choose a case...</option>
                                        {selectableCases.length === 0 ? (
                                            <option disabled>No active cases available</option>
                                        ) : (
                                            selectableCases.map(c => (
                                                <option key={c._id} value={c._id}>
                                                    {c.userDetails?.name} — {c.caseId || "No Case ID"}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                            )}

                            <div className="planner-form-grid">
                                <div className="planner-form-group">
                                    <label>
                                        <FaCalendarCheck size={14} />
                                        <span>Hearing Date & Time *</span>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        className="planner-form-input"
                                        value={form.hearingDateTime}
                                        onChange={e =>
                                            setForm({ ...form, hearingDateTime: e.target.value })
                                        }
                                    />
                                </div>

                                <div className="planner-form-group">
                                    <label>
                                        <FaCheck size={14} />
                                        <span>Status</span>
                                    </label>
                                    <select
                                        className="planner-form-select"
                                        value={form.statusAtHearing}
                                        onChange={e =>
                                            setForm({ ...form, statusAtHearing: e.target.value })
                                        }
                                    >
                                        <option value="INCOMPLETE">Incomplete</option>
                                        <option value="COMPLETED">Completed</option>
                                        <option value="CANCELLED">Cancelled</option>
                                    </select>
                                </div>
                            </div>

                            <div className="planner-form-group">
                                <label>
                                    <FaGavel size={14} />
                                    <span>Court Name *</span>
                                </label>
                                <input
                                    type="text"
                                    className="planner-form-input"
                                    placeholder="Enter court name"
                                    value={form.courtName}
                                    onChange={e => setForm({ ...form, courtName: e.target.value })}
                                />
                            </div>

                            <div className="planner-form-grid">
                                <div className="planner-form-group">
                                    <label>
                                        <FaMapMarkerAlt size={14} />
                                        <span>Court Location</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="planner-form-input"
                                        placeholder="Enter location link"
                                        value={form.courtLocation}
                                        onChange={e => setForm({ ...form, courtLocation: e.target.value })}
                                    />
                                </div>

                                <div className="planner-form-group">
                                    <label>
                                        <FaUserTie size={14} />
                                        <span>Judge Name</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="planner-form-input"
                                        placeholder="Enter judge name"
                                        value={form.judgeName}
                                        onChange={e => setForm({ ...form, judgeName: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="planner-form-group">
                                <label>
                                    <FaComments size={14} />
                                    <span>Remarks</span>
                                </label>
                                <textarea
                                    className="planner-form-textarea"
                                    placeholder="Enter any remarks or notes..."
                                    value={form.remarks}
                                    onChange={e => setForm({ ...form, remarks: e.target.value })}
                                    rows="3"
                                />
                            </div>
                        </div>

                        <div className="planner-modal-footer">
                            <button
                                className="planner-modal-cancel"
                                onClick={() => {
                                    setShowAddModal(false);
                                    setEditing(null);
                                    resetForm();
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="planner-modal-submit"
                                onClick={editing ? saveEdit : addHearing}
                            >
                                {editing ? (
                                    <>
                                        <FaEdit size={16} />
                                        <span>Update Hearing</span>
                                    </>
                                ) : (
                                    <>
                                        <FaPlus size={16} />
                                        <span>Schedule Hearing</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}